import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { Download, Upload, FileSpreadsheet, X } from "lucide-react";
import { generateArthatrackExcelAPI } from "../../actioncreators/actioncreators";
import AttendanceReportHistory from "./AttendanceReportHistory";

function ArthatrackExcelReport() {
  const now = new Date();
  const defaultMonth =
    now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");

  const [month, setMonth] = useState(defaultMonth);

  const MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];
  const currentYear = now.getFullYear();
  const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const selectedYear  = parseInt(month.split('-')[0], 10);
  const selectedMonth = parseInt(month.split('-')[1], 10);

  const handleYearChange  = (y) => setMonth(`${y}-${String(selectedMonth).padStart(2, '0')}`);
  const handleMonthSelect = (m) => setMonth(`${selectedYear}-${String(m).padStart(2, '0')}`);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  // Incrementing this key forces AttendanceReportHistory to re-fetch after a successful generate.
  const [historyKey, setHistoryKey] = useState(0);
  const fileInputRef = useRef(null);

  const ALLOWED_EXTS = ["xlsx", "xls"];
  const ALLOWED_MIMES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel",                                           // .xls
    "application/octet-stream",                                           // some browsers use this for .xls
  ];
  const MAX_FILE_SIZE_MB = 20;

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const ext = selected.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Invalid file type ".${ext}". Only .xlsx and .xls files are allowed.`);
      e.target.value = "";
      return;
    }

    if (selected.type && !ALLOWED_MIMES.includes(selected.type)) {
      toast.error("File content does not match a valid Excel format. Please upload a genuine .xlsx or .xls file.");
      e.target.value = "";
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please upload a smaller file.`);
      e.target.value = "";
      return;
    }

    if (selected.size === 0) {
      toast.error("The selected file is empty. Please upload a valid Excel file.");
      e.target.value = "";
      return;
    }

    setFile(selected);
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleGenerate = async () => {
    if (!file) { toast.error("Please upload an Excel file first."); return; }
    if (!month) { toast.error("Please select a month."); return; }
    const [yearStr] = month.split('-');
    const year = parseInt(yearStr, 10);
    if (yearStr.length !== 4 || year < 1900 || year > 2100) {
      toast.error("Please enter a valid year (1900–2100).");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", month);

      const response = await generateArthatrackExcelAPI(formData);

      const baseName = file.name.replace(/\.[^.]+$/, "");
      const outName = `${baseName}_arthatrack.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel file downloaded successfully.");
      // Refresh the history table below
      setHistoryKey((k) => k + 1);
    } catch (err) {
      let message = "Failed to generate report. Please try again.";
      try {
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json?.message) message = json.message;
        }
      } catch (_) {}
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <h5 className="fw-bold mb-3" style={{ color: "#FF8800" }}>
        Generate Arthatrack Attendance Sheet
      </h5>

      {/* ── Generate form ── */}
      <div className="card shadow-sm p-3 mb-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">

          {/* Upload zone */}
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            {!file ? (
              <div
                className="d-flex align-items-center gap-2 border border-2 border-dashed rounded px-3 py-2"
                style={{ borderColor: "#FF8800", cursor: "pointer", background: "#fffaf5", height: 44 }}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <Upload size={18} color="#FF8800" style={{ flexShrink: 0 }} />
                <span className="text-muted text-truncate" style={{ fontSize: 13 }}>
                  Click to upload <strong>.xlsx</strong> or <strong>.xls</strong>
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="d-none"
                  onChange={handleFileChange}
                />
              </div>
            ) : (
              <div
                className="d-flex align-items-center gap-2 border rounded px-3 py-2"
                style={{ background: "#f8f9fa", height: 44 }}
              >
                <FileSpreadsheet size={18} color="#217346" style={{ flexShrink: 0 }} />
                <span className="fw-semibold text-truncate flex-grow-1" style={{ fontSize: 13 }} title={file.name}>
                  {file.name}
                </span>
                <button
                  className="btn btn-sm btn-outline-danger p-0"
                  style={{ width: 24, height: 24, lineHeight: 1 }}
                  onClick={handleRemoveFile}
                  title="Remove file"
                >
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Month picker — two selects so dropdown closes immediately on selection */}
          <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
            <select
              className="form-select"
              style={{ height: 44, fontSize: 13, width: 130 }}
              value={selectedMonth}
              onChange={(e) => handleMonthSelect(Number(e.target.value))}
            >
              {MONTHS.map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              className="form-select"
              style={{ height: 44, fontSize: 13, width: 90 }}
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Generate button */}
          <div style={{ flexShrink: 0 }}>
            <button
              className="btn d-flex align-items-center gap-2"
              style={{
                backgroundColor: loading || !file ? "#ccc" : "#FF8800",
                color: "#fff",
                fontWeight: 600,
                height: 44,
                cursor: loading || !file ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}
              onClick={handleGenerate}
              disabled={loading || !file}
            >
              <Download size={15} />
              {loading ? "Generating…" : "Generate & Download Excel"}
            </button>
          </div>
        </div>

        {file && (
          <small className="text-muted d-block mt-2">
            Downloads <strong>{file.name.replace(/\.[^.]+$/, "")}_arthatrack.xlsx</strong>
          </small>
        )}
      </div>


      {/* ── History table ── */}
      <AttendanceReportHistory key={historyKey} embedded />
    </div>
  );
}

export default ArthatrackExcelReport;
